// A team's primary contact: who to reach about it, and the method that person
// chose. These rules mirror api/internal/platform/contact.go so a form can say
// what's wrong before a round trip — the API is still the authority.

// carousel: the only method for a player under 18. Leagues message them
// through Carousel and never see an address, so its value is empty and it is
// never a free-text option.
export type ContactMethod = 'discord' | 'phone' | 'email' | 'carousel'

export interface PrimaryContact {
  name: string
  method: ContactMethod
  value: string
  /** Set when the contact is a person on the team, by a method they verified. */
  user_id?: string
  /** The person proved they own this Discord, email or phone. */
  verified?: boolean
}

/** A person on the team, by one of their verified methods — what a club team picks. */
export interface ContactChoice {
  user_id: string
  method: ContactMethod
}

/** Someone who could be a team's contact, and the methods they've verified. */
export interface ContactPersonOption {
  user_id: string
  name: string
  role?: string
  methods: ContactMethod[]
  /** Under 18: never a team's contact, whatever they've verified. */
  minor?: boolean
}

export type ContactField = 'name' | 'method' | 'value'

export type ContactErrors = Partial<Record<ContactField, string>>

/** The methods a free-text contact can use (never carousel). */
export const CONTACT_METHODS: { value: ContactMethod; label: string }[] = [
  { value: 'discord', label: 'Discord' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
]

export const CONTACT_NAME_MAX = 80

const E164 = /^\+[1-9]\d{1,14}$/
const PHONE_STRIP = /[\s\-().]/g
const DISCORD_USERNAME = /^[a-z0-9_.]{2,32}$/
const DISCORD_LEGACY_TAG = /#\d{1,4}$/
// Deliberately loose: one @, something either side, a dot in the domain. The
// API parses it properly; this only catches the obvious slip.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A blank contact for a form, defaulting to Discord. */
export function emptyContact(method: ContactMethod = 'discord'): PrimaryContact {
  return { name: '', method, value: '' }
}

/** The contact as the API will store it: trimmed, formatting stripped. */
export function normalizeContact(c: PrimaryContact): PrimaryContact {
  // Free text only — a verified person's contact isn't edited as text.
  const name = c.name.trim().split(/\s+/).filter(Boolean).join(' ')
  let value = c.value.trim()
  if (c.method === 'email') value = value.toLowerCase()
  if (c.method === 'phone') value = value.replace(PHONE_STRIP, '')
  if (c.method === 'discord') value = value.replace(/^@/, '').toLowerCase().replace(DISCORD_LEGACY_TAG, '')
  return { name, method: c.method, value }
}

/** Every problem with a contact, by field. Empty when it can be saved. */
export function validateContact(c: PrimaryContact): ContactErrors {
  const n = normalizeContact(c)
  const errors: ContactErrors = {}
  if (!n.name) errors.name = 'Say who the contact is'
  else if ([...n.name].length > CONTACT_NAME_MAX) errors.name = `Contact name must be ${CONTACT_NAME_MAX} characters or fewer`

  if (!n.value) errors.value = 'Add how to reach the contact'
  else if (n.method === 'phone' && !E164.test(n.value)) errors.value = 'Phone number needs a country code, like +14155551234'
  else if (n.method === 'email' && !EMAIL.test(n.value)) errors.value = "That email address doesn't look right"
  else if (n.method === 'discord' && (!DISCORD_USERNAME.test(n.value) || n.value.includes('..'))) {
    errors.value = 'Discord usernames are 2–32 characters: letters, numbers, _ and .'
  }
  return errors
}

export function isContactValid(c: PrimaryContact): boolean {
  return Object.keys(validateContact(c)).length === 0
}

/** Whether two contacts are the same person by the same method, once normalized. */
export function contactsEqual(a: PrimaryContact | null | undefined, b: PrimaryContact | null | undefined): boolean {
  if (!a || !b) return !a && !b
  const x = normalizeContact(a)
  const y = normalizeContact(b)
  return x.name === y.name && x.method === y.method && x.value === y.value
}

/**
 * Which contact field an API error points at. The API reports a bad contact as
 * an RFC 7807 problem whose errors[].location is "body.contact.value" and so
 * on; "body.contact" alone (no contact sent) maps to the name, the first thing
 * to fill in.
 */
export function contactFieldFromLocation(location: string | undefined): ContactField | null {
  if (!location) return null
  const m = /(?:^|\.)contact(?:\.(name|method|value))?$/.exec(location)
  if (!m) return null
  return (m[1] as ContactField | undefined) ?? 'name'
}

export function contactMethodLabel(method: ContactMethod): string {
  if (method === 'carousel') return 'Carousel'
  return CONTACT_METHODS.find(m => m.value === method)?.label ?? method
}

/**
 * The contact problems in a refused save, by field — or null when the refusal
 * wasn't about the contact. Takes the parsed RFC 7807 body (ApiError.body).
 */
export function contactErrorsFromProblem(body: unknown): ContactErrors | null {
  const errors = (body as { errors?: unknown } | null)?.errors
  if (!Array.isArray(errors)) return null
  const out: ContactErrors = {}
  for (const e of errors as { location?: string; message?: string }[]) {
    const field = contactFieldFromLocation(e.location)
    if (field && !out[field]) out[field] = e.message || 'Check this field'
  }
  return Object.keys(out).length ? out : null
}
